import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
} from "@mui/material";
import { useQuery } from "react-query";
import logo from "../../utils/images/ridePlus.png";
import AuthContext from "../../context/AuthProvider";
import axios from "../../api/axios";
import { queryClient } from "../../App";
import RequestOtp from "./requestOtp";
import Consent from "../consent/consent";
import TenantSelection from "../tenants/tenant-selection";
import { useSnackbar } from "notistack";
import { phoneRegex } from "../../utils/validationPattern";
import useOtpResendCooldown from "../../hooks/useOtpResendCooldown";
import OtpResendButton from "../../components/otpResendButton";
import { BASE_URL, VERSION } from "../../utils/config";

function SignInWithPhone({ consentId }) {
  const [openDialog, setOpenDialog] = useState(false);
  const userInput = useRef({});
  const [requestOtp, setRequestOtp] = useState(false);
  const [showTenantSelection, setShowTenantSelection] = useState(false);
  const [userIdentifier, setUserIdentifier] = useState("");
  const [identifierType, setIdentifierType] = useState("phone"); // "phone" or "email"

  // OTP cooldown hook for login attempts
  const cooldownHook = useOtpResendCooldown("login", userInput.current);

  const navigate = useNavigate();
  const location = useLocation();

  // Get Telegram params from location state
  const enabledAuthMethods = useMemo(
    () => location.state?.enabledAuthMethods || [],
    [location.state?.enabledAuthMethods]
  );
  const botUserName = location.state?.botUserName || "";
  const consentIdFromState = location.state?.consentId || consentId;
  const tenantIdFromState = location.state?.tenantId;

  // USEFORM HOOK
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm({
    mode: "onSubmit",
  });

  userInput.current = watch("phone", "");

  const {
    auth,
    persist,
    redirectUri,
    currentTenant,
    getTenantIdFromUrl,
    isAuthInitializing,
  } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const tenantId =
    tenantIdFromState || currentTenant?.id || getTenantIdFromUrl();
  // const { data: identityProviders } = useQuery(
  //   ["registered_identity_providers"],
  //   () => {
  //     return axios.get(`/registeredIdentityProviders`, {
  //       params: {
  //         page: -1,
  //         ...(tenantId && { tenant_id: tenantId }),
  //       },
  //     });
  //   }
  // );

  const { isFetching, error } = useQuery(
    ["login_with_phone_otp"],
    () =>
      axios.get("/otp", {
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          type: "login",
          phone: userInput.current,
          tenant_id: tenantId,
        },
      }),
    {
      onSuccess: () => {
        cooldownHook.startCooldown(); // Start cooldown after successful OTP request
        setRequestOtp(false);
        setOpenDialog(true);
      },
      enabled: requestOtp,
    }
  );

  const onSubmit = () => {
    if (errors?.phone === undefined && cooldownHook.canResend) {
      const tenantId = currentTenant?.id || getTenantIdFromUrl();

      // If no tenant_id in URL, show tenant selection
      if (!tenantId) {
        setUserIdentifier(userInput.current);
        setIdentifierType("phone");
        setShowTenantSelection(true);
        return;
      }

      // Otherwise proceed with normal OTP request
      setRequestOtp(true);
    }
  };

  useEffect(() => {
    localStorage.setItem("persist", persist);
    queryClient.clear();
  }, [persist]);

  // Ref callback to load Telegram widget when container is mounted
  const telegramContainerRef = useCallback(
    (container) => {
      if (!container) return;

      if (
        !enabledAuthMethods.includes("telegram") ||
        !botUserName ||
        !consentIdFromState ||
        !tenantId ||
        !BASE_URL ||
        !VERSION
      ) {
        console.log("Telegram widget not loading. Conditions:", {
          hasTelegram: enabledAuthMethods.includes("telegram"),
          botUserName,
          consentIdFromState,
          tenantId,
        });
        return;
      }

      // Clean up any existing script first
      const existingScript = container.querySelector(
        'script[src*="telegram-widget"]'
      );
      if (existingScript) {
        return; // Already loaded
      }

      // Clean bot username (remove @ if present)
      const cleanBotName = botUserName.startsWith("@")
        ? botUserName.substring(1)
        : botUserName;

      const script = document.createElement("script");
      script.src = "https://telegram.org/js/telegram-widget.js?22";
      script.async = true;
      script.setAttribute("data-telegram-login", cleanBotName);
      script.setAttribute("data-size", "large");
      script.setAttribute(
        "data-auth-url",
        `${BASE_URL}/${VERSION}/oauth/telegram/consent/${consentIdFromState}/tenant/${tenantId}/authorize`
      );
      script.setAttribute("data-request-access", "write");

      console.log("Loading Telegram widget with bot:", cleanBotName);
      container.appendChild(script);
    },
    [enabledAuthMethods, botUserName, consentIdFromState, tenantId]
  );

  // Handle tenant selection
  const handleTenantSelected = (tenant) => {
    // After tenant selection, proceed with OTP request
    setShowTenantSelection(false);
    setOpenDialog(true);
    setRequestOtp(true);
  };

  const handleBackFromTenantSelection = () => {
    setShowTenantSelection(false);
    setUserIdentifier("");
  };

  useEffect(() => {
    if (error) {
      if (error?.response?.data?.error?.field_error?.length > 0) {
        error?.response?.data?.error?.field_error?.map((msg) => {
          return enqueueSnackbar(msg.description || "Network Error!", {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 2000,
          });
        });
      } else {
        enqueueSnackbar(
          error?.response?.data?.error?.message ||
            error?.message ||
            "Network Error!",
          {
            variant: "error",
          }
        );
      }
      setRequestOtp(false);
    }
    queryClient.clear();
  }, [enqueueSnackbar, error, setRequestOtp]);
  // RENDER
  if (isAuthInitializing && persist) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress size={40} sx={{ color: "secondary.main" }} />
      </Box>
    );
  }

  return auth?.access_token ? (
    redirectUri ? (
      <Consent consentId={consentId} />
    ) : (
      navigate("/")
    )
  ) : showTenantSelection ? (
    <TenantSelection
      userIdentifier={userIdentifier}
      identifierType={identifierType}
      onBack={handleBackFromTenantSelection}
      onTenantSelected={handleTenantSelected}
    />
  ) : (
    <>
      {openDialog ? (
        <RequestOtp
          openDialog={openDialog}
          userInput={userInput.current}
          setOpenDialog={setOpenDialog}
          cooldownHook={cooldownHook}
        />
      ) : (
        <Dialog
          open={!openDialog}
          sx={{
            "& .MuiDialog-paper": {
              boxShadow: "0px 1px 1px 1px white",
            },
          }}
          BackdropProps={{
            style: {
              backgroundColor: "white",
            },
          }}
          PaperProps={{
            sx: {
              width: { lg: "25%", md: "40%", xs: "100%", sm: "45%" },
              height: "auto",
            },
          }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <DialogContent
            sx={{
              px: 0,
              pb: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 2,
                mb: 2,
              }}
            >
              <Avatar
                sx={{ width: "140px", height: "100px", m: 1, borderRadius: 0 }}
                src={logo}
              />
            </Box>
            <Controller
              name="phone"
              control={control}
              rules={{
                required: "Phone is required",
                pattern: {
                  value: phoneRegex,
                  message: "Invalid input",
                },
              }}
              render={({ field }) => (
                <TextField
                  size="small"
                  sx={{
                    mt: 1,
                    mb: 2,
                  }}
                  fullWidth
                  label="Phone No"
                  autoFocus
                  helperText={errors?.phone ? errors?.phone?.message : ""}
                  error={errors?.phone}
                  {...field}
                />
              )}
            />
          </DialogContent>
          <DialogActions
            sx={{
              display: "flex",
              flexDirection: "column",
              px: 0,
            }}
          >
            <Button
              fullWidth
              sx={{
                bgcolor: "secondary.main",
                ":hover": {
                  bgcolor: "secondary.main",
                },
                color: "#fff",
                mb: 1,
                fontWeight: "bold",
                borderRadius: 6,
              }}
              type="submit"
              size="large"
              disabled={isFetching || !cooldownHook.canResend}
            >
              {cooldownHook.isOnCooldown
                ? `Login (${cooldownHook.formattedTime})`
                : "Login"}
              {isFetching && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: "#fff",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Button>
            {(cooldownHook.isOnCooldown || cooldownHook.isBlocked) && (
              <Box sx={{ mt: 1, mb: 2 }}>
                <OtpResendButton
                  cooldownHook={cooldownHook}
                  onResend={() => setRequestOtp(true)}
                  isLoading={isFetching}
                  size="small"
                  showMessage={true}
                />
              </Box>
            )}
            <Box
              sx={{
                mt: 1,
                display: "flex",
                flexDirection: { sm: "row", xs: "column" },
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{ textAlign: "center", fontWeight: "medium", pr: 1 }}
              >
                Don't have an account?
              </Typography>
              <Button
                sx={{
                  color: "primary.main",
                  fontWeight: "bold",
                  textTransform: "none",
                  fontSize: 16,
                }}
                onClick={() => navigate("/register")}
              >
                Create Account
              </Button>
            </Box>
            <Box
              sx={{
                mt: 1,
                display: "flex",
                flexDirection: { sm: "row", xs: "column" },
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{ textAlign: "center", fontWeight: "medium", pr: 1 }}
              >
                Need a new tenant?
              </Typography>
              <Button
                sx={{
                  color: "secondary.main",
                  fontWeight: "bold",
                  textTransform: "none",
                  fontSize: 16,
                }}
                onClick={() => navigate("/tenant-registration")}
              >
                Register Tenant
              </Button>
            </Box>
            {/* {identityProviders?.data?.data?.length > 1 && (
              <>
                <Divider> Or </Divider>
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  {identityProviders?.data?.data?.map((IP) => (
                    <Button
                      key={IP?.id}
                      fullWidth
                      size="large"
                      variant="contained"
                      sx={{
                        backgroundColor: "primary.paper",
                        textTransform: "none",
                        mb: 1.5,
                        borderRadius: 6,
                      }}
                    >
                      <Avatar
                        sx={{ width: 30, height: 30, mr: 1 }}
                        src={IP.logo_uri}
                      />
                      <Typography
                        sx={{
                          textAlign: "center",
                          mt: 0.5,
                          fontSize: 14,
                          fontWeight: "bold",
                        }}
                      >
                        Sign In With {IP?.name}
                      </Typography>
                    </Button>
                  ))}
                </Box>
              </>
            )} */}
            {enabledAuthMethods.includes("telegram") && botUserName && (
              <>
                <Divider sx={{ mt: 2 }}> Or </Divider>
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <div ref={telegramContainerRef}></div>
                </Box>
              </>
            )}
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
export default SignInWithPhone;
